import React from "react";
import { ShieldCheckIcon as ShieldCheck, CrownIcon as Crown } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";

/**
 * Plan-tier verified badge — Pro (gold-foil cream) or Kennel Elite
 * (obsidian + foil). Renders next to kennel names in marketplace
 * cards, public kennel profiles, and Pawprint posts.
 *
 * The shadow + inset highlight combo gives the pill physical depth so
 * it reads as a stamped medallion rather than a flat status tag.
 *
 * Props:
 *   plan — "basic" | "pro" | "kennel_elite" | null
 *   size — icon px (default 12). Pill auto-scales padding.
 */
export default function VerifiedBadge({ plan, size = 12, className = "" }) {
  if (!plan || plan === "basic") return null;

  if (plan === "kennel_elite") {
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase ${className}`}
        title={isNativeApp() ? "Verified breeder" : "Kennel Elite — top-tier verified breeder"}
        data-testid="badge-kennel-elite"
        style={{
          // Obsidian body with a hint of cylinder shading from the top
          // bevel to the bottom bevel — reads as a polished tag, not
          // a flat block.
          background: "linear-gradient(180deg, #2D2D2D 0%, #0E0E0E 55%, #1A1A1A 100%)",
          color: "#E8C75E",
          // Top edge gets a 1px gold highlight; bottom edge a 1px black
          // shadow → simulates a stamped/embossed surface. Outer drop
          // shadow gives the badge lift off the card.
          boxShadow:
            "inset 0 1px 0 rgba(232,199,94,0.4), inset 0 -1px 0 rgba(0,0,0,0.6), 0 1px 2px rgba(14,14,14,0.18)",
          textShadow: "0 1px 0 rgba(0,0,0,0.45)",
        }}
      >
        <Crown size={size} weight="fill" style={{ filter: "drop-shadow(0 1px 0 rgba(0,0,0,0.4))" }} />
        {isNativeApp() ? "Verified" : "Elite"}
      </span>
    );
  }

  // Pro — cream body with a soft gold-foil gradient + the same
  // inset-highlight/shadow vocabulary as Elite for visual cohesion.
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase ${className}`}
      title={isNativeApp() ? "Verified breeder" : "Verified Pro breeder"}
      data-testid="badge-pro"
      style={{
        background: "linear-gradient(180deg, #FBF3D9 0%, #F4E5B0 100%)",
        color: "#7A5C00",
        border: "1px solid rgba(201,162,39,0.55)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(122,92,0,0.18), 0 1px 2px rgba(122,92,0,0.12)",
      }}
    >
      <ShieldCheck size={size} weight="duotone" />
      {isNativeApp() ? "Verified" : "Pro"}
    </span>
  );
}
